import { paths } from '@common/types';
import { ColumnSelectionFilter } from './ColumnSelectionFilter';
import {
  useDeleteWorklistTablePreferencesMutation,
  useWorklistTablePreferencesQuery,
  useUpdateWorklistTablePreferencesMutation,
} from '@common/cache';
import { Button } from '@mui/material';

type WorklistTableColumns =
  paths['/preferences/task-table']['get']['responses']['200']['content']['application/json']['columns'];

type WorklistTableColumnKey = keyof WorklistTableColumns;

const WORKLIST_TABLE_COLUMN_LABELS: Record<WorklistTableColumnKey, string> = {
  task: 'Task',
  category: 'Category',
  patient: 'Patient',
  priority: 'Priority',
  due_date: 'Due date',
  status: 'Status',
  created_date: 'Created date',
  last_updated_at: 'Last updated',
  provider: 'Provider',
  location: 'Location',
  assignment: 'Assigned to',
};

export const WorklistTableColumnSelectionFilter = ({
  hideText,
}: {
  hideText?: boolean;
}) => {
  const { data: preferences, isPending: isFetchPending } =
    useWorklistTablePreferencesQuery();
  const updatePreferencesMutation = useUpdateWorklistTablePreferencesMutation();
  const deletePreferencesMutation = useDeleteWorklistTablePreferencesMutation();

  const handleSelectionChange = async (selection: WorklistTableColumns) => {
    updatePreferencesMutation.mutateAsync({ columns: selection });
  };

  const handleReset = async () => {
    if (!preferences?.columns) return;

    const resetColumns = Object.fromEntries(
      Object.entries(preferences.columns).map(([key, config]) => [
        key,
        {
          ...config,
          column_display: 'display',
        },
      ]),
    ) as WorklistTableColumns;

    await updatePreferencesMutation.mutateAsync({ columns: resetColumns });
  };

  return (
    preferences?.columns && (
      <ColumnSelectionFilter
        name='worklist_table_columns'
        label='Columns'
        hideText={hideText}
        columns={preferences?.columns}
        columnLabels={WORKLIST_TABLE_COLUMN_LABELS}
        onChange={handleSelectionChange}
        footerAction={
          <Button color='primary' onClick={handleReset}>
            RESET
          </Button>
        }
        loading={
          updatePreferencesMutation.isPending ||
          deletePreferencesMutation.isPending ||
          isFetchPending
        }
      />
    )
  );
};
